import * as React from "react"
import { useAdminView } from "@/contexts/AdminViewContext"

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

interface MobileInfo {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  deviceType: 'mobile' | 'tablet' | 'desktop'
  isIOS: boolean
  isAndroid: boolean
  isTouchDevice: boolean
  screenWidth: number
  orientation: 'portrait' | 'landscape'
}

export function useEnhancedMobile(): MobileInfo {
  const adminView = useAdminView();
  const [mobileInfo, setMobileInfo] = React.useState<MobileInfo>(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        deviceType: 'desktop',
        isIOS: false,
        isAndroid: false,
        isTouchDevice: false,
        screenWidth: 1920,
        orientation: 'landscape'
      }
    }

    // Check for admin override in localStorage for initial render
    const storedOverride = localStorage.getItem('admin_view_override');
    const width = (storedOverride === 'mobile' ? 375 : storedOverride === 'tablet' ? 768 : window.innerWidth);
    
    const userAgent = navigator.userAgent
    const isMobile = width < MOBILE_BREAKPOINT
    const isTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
    const isDesktop = width >= TABLET_BREAKPOINT

    return {
      isMobile,
      isTablet,
      isDesktop,
      deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
      isIOS: /iPad|iPhone|iPod/.test(userAgent),
      isAndroid: /Android/.test(userAgent),
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      screenWidth: width,
      orientation: storedOverride ? 'portrait' : (window.innerHeight > window.innerWidth ? 'portrait' : 'landscape')
    }
  })

  React.useEffect(() => {
    const updateMobileInfo = () => {
      // Check for admin override first
      if (adminView.isOverrideActive && adminView.simulatedWidth) {
        const width = adminView.simulatedWidth
        const userAgent = navigator.userAgent
        const isMobile = width < MOBILE_BREAKPOINT
        const isTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
        const isDesktop = width >= TABLET_BREAKPOINT

        const overrideInfo: MobileInfo = {
          isMobile,
          isTablet,
          isDesktop,
          deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
          isIOS: /iPad|iPhone|iPod/.test(userAgent),
          isAndroid: /Android/.test(userAgent),
          isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
          screenWidth: width,
          orientation: 'portrait'
        }

        console.log('Admin view override active:', adminView.viewOverride, overrideInfo)
        setMobileInfo(overrideInfo)
        return
      }

      // Use actual window dimensions
      const width = window.innerWidth
      const userAgent = navigator.userAgent
      const isMobile = width < MOBILE_BREAKPOINT
      const isTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
      const isDesktop = width >= TABLET_BREAKPOINT

      const newInfo: MobileInfo = {
        isMobile,
        isTablet,
        isDesktop,
        deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
        isIOS: /iPad|iPhone|iPod/.test(userAgent),
        isAndroid: /Android/.test(userAgent),
        isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        screenWidth: width,
        orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
      }

      console.log('Enhanced mobile detection:', newInfo)
      setMobileInfo(newInfo)
    }

    const handleResize = () => updateMobileInfo()
    const handleOrientationChange = () => setTimeout(updateMobileInfo, 100)

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleOrientationChange)
    
    // Initial update
    updateMobileInfo()

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [adminView.isOverrideActive, adminView.simulatedWidth, adminView.viewOverride])

  return mobileInfo
}